import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import App from './App';
import { initializeAuthListener } from './services/supabase';
import './styles/global.css';

// Initialize Supabase auth listener immediately with proper error handling
initializeAuthListener().catch(error => {
  console.error('❌ Critical auth initialization error:', error);
});

// Defer non-critical initializations
if (typeof window !== 'undefined') {
  // Initialize security measures (deferred)
  import('./middleware/security-init').then(({ initializeApplicationSecurity }) => {
    initializeApplicationSecurity();
  }).catch(error => {
    console.warn('⚠️ Security initialization failed:', error);
  });

  // Initialize performance monitoring only in development (deferred)
  if (process.env.NODE_ENV === 'development') {
    import('./utils/performance').then(({ initializePerformanceMonitoring }) => {
      initializePerformanceMonitoring();
    }).catch(error => {
      console.warn('⚠️ Performance monitoring failed:', error);
    });
  }
}

// Detect stale-deployment chunk errors (HTML served instead of JS)
function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    (error.name === 'SyntaxError' && msg.includes("Unexpected token '<'")) ||
    (error.name === 'TypeError' && msg.includes('Failed to fetch'))
  );
}

function handleStaleChunkReload(): boolean {
  const reloadKey = 'chunk-error-reload';
  const lastReload = sessionStorage.getItem(reloadKey);
  if (!lastReload || Date.now() - Number(lastReload) > 10_000) {
    sessionStorage.setItem(reloadKey, String(Date.now()));
    window.location.reload();
    return true;
  }
  return false;
}

// Error handling for uncaught errors
window.addEventListener('error', (event) => {
  if (isStaleChunkError(event.error) && handleStaleChunkReload()) return;
  console.error('Uncaught error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  if (isStaleChunkError(event.reason) && handleStaleChunkReload()) return;
  console.error('Unhandled promise rejection:', event.reason);
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('❌ CRITICAL: Root element not found in DOM!');
  throw new Error('Root element #root not found');
}

const root = ReactDOM.createRoot(rootElement as HTMLElement);

try {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} catch (error) {
  console.error('❌ CRITICAL: Failed to render React app:', error);
  throw error;
}
